import { InputRef } from "antd";
import { FormItemProps } from "antd/lib";

import {
  createContext,
  LegacyRef,
  ReactNode,
  useContext,
  useState,
} from "react";
import { SupportedLanguages } from "gql/graphql";
import { LANGUAGE_TO_PLACEHOLDER } from "@/shared/form/localized-value/utils/placeholder-utils";
import { Rule } from "../../form";
import {
  LocalizedControlName,
  useLocalizedFields,
} from "@/shared/form/localized-value/model/localized-field";

export type RenderFn = (props: {
  placeholder: string;
  ref?: LegacyRef<InputRef>;
}) => ReactNode;

type ContextInput = {
  render: RenderFn;
  inputRef: LegacyRef<InputRef>;
  formItemProps: FormItemProps;
  myLocaleRules?: Rule[];
  translationContext?: string;
};

type ContextProps = ContextInput & {
  languages: SupportedLanguages[];
  languageToControlName: (language: SupportedLanguages) => LocalizedControlName;
  languageToPlaceholder: (language: SupportedLanguages) => string;
  currentLanguages: SupportedLanguages;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  myLocaleRules?: Rule[];
};

const Context = createContext<ContextProps>({} as ContextProps);

export function ContextProvider({
  children,

  formItemProps,
  inputRef,
  render,
  myLocaleRules,
  translationContext,
}: ContextInput & {
  children: (isOpen: boolean, close: () => void) => JSX.Element;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const { languages, localeToControlName, currentLanguages } =
    useLocalizedFields();

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  return (
    <Context.Provider
      value={{
        languages,
        formItemProps,
        inputRef,
        render,
        languageToControlName: localeToControlName,
        languageToPlaceholder: LANGUAGE_TO_PLACEHOLDER,
        currentLanguages,
        isOpen,
        open,
        close,
        myLocaleRules,
        translationContext,
      }}
    >
      {children(isOpen, close)}
    </Context.Provider>
  );
}

export function useLocalizedControl() {
  return useContext(Context);
}
